import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import {
  MessageCircle,
  X,
  Send,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Loader2,
  Bot,
  User,
  Trash2,
  Minus,
  StopCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/marketplace-bot`;

/* ═══════════════════════════════════════════════════════════════════════════
   Markdown renderer (simplified version of ChatMessage for the compact widget)
   ═══════════════════════════════════════════════════════════════════════════ */
const BotMarkdown = memo(({ content }) => {
  const components = useMemo(
    () => ({
      p: ({ children }) => <p className="mb-1.5 last:mb-0 leading-relaxed">{children}</p>,
      ul: ({ children }) => <ul className="list-disc list-inside mb-1.5 space-y-0.5">{children}</ul>,
      ol: ({ children }) => <ol className="list-decimal list-inside mb-1.5 space-y-0.5">{children}</ol>,
      li: ({ children }) => <li className="leading-relaxed">{children}</li>,
      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
      a: ({ href, children }) => (
        <a href={href} className="text-[#F87217] underline hover:text-[#C65B12]" target="_blank" rel="noreferrer">
          {children}
        </a>
      ),
      code: ({ inline, children }) =>
        inline !== false ? (
          <code className="bg-slate-200 text-slate-800 px-1 py-0.5 rounded text-xs">{children}</code>
        ) : (
          <pre className="bg-slate-800 text-slate-100 p-2 rounded text-xs overflow-x-auto my-1.5">
            <code>{children}</code>
          </pre>
        ),
      h1: ({ children }) => <h4 className="font-semibold text-sm mt-2 mb-1">{children}</h4>,
      h2: ({ children }) => <h4 className="font-semibold text-sm mt-2 mb-1">{children}</h4>,
      h3: ({ children }) => <h4 className="font-semibold text-sm mt-2 mb-1">{children}</h4>,
      blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-[#F87217] pl-2 italic my-1.5 text-slate-600">{children}</blockquote>
      ),
    }),
    []
  );
  return (
    <ReactMarkdown components={components}>
      {content}
    </ReactMarkdown>
  );
});

/* ═══════════════════════════════════════════════════════════════════════════
   Message bubble component
   ═══════════════════════════════════════════════════════════════════════════ */
const MessageBubble = memo(({ msg }) => {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      {!isUser && (
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-[#F87217] to-[#FF9A4E] flex items-center justify-center mt-0.5">
          <Bot className="w-3.5 h-3.5 text-white" />
        </div>
      )}
      <div
        className={`max-w-[82%] px-3 py-2 rounded-xl text-[13px] leading-relaxed ${isUser
          ? "bg-[#F87217] text-white rounded-br-sm"
          : "bg-slate-100 text-slate-800 rounded-bl-sm"
          }`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{msg.content}</p>
        ) : (
          <BotMarkdown content={msg.content} />
        )}
        {msg.transcribed && (
          <p className="text-[10px] mt-1 opacity-70 italic">🎤 "{msg.transcribed}"</p>
        )}
      </div>
      {isUser && (
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center mt-0.5">
          <User className="w-3.5 h-3.5 text-slate-600" />
        </div>
      )}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════════════════════
   Main Marketplace Bot Widget
   ═══════════════════════════════════════════════════════════════════════════ */
export default function MarketplaceBot() {
  const { isAuthenticated, token, user } = useAuth();

  // ── State ──
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  // ── Refs ──
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const ttsAudioRef = useRef(null);          // edge-tts Audio element
  const prevUserIdRef = useRef(null);         // track user changes for isolation

  // ── Auth header ──
  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // ── Stop any TTS playback ──
  const stopSpeaking = useCallback(() => {
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current.currentTime = 0;
      ttsAudioRef.current.src = "";
      ttsAudioRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  // ── User isolation: reset bot state when user changes ──
  useEffect(() => {
    const currentUserId = user?.id || null;
    if (prevUserIdRef.current !== null && prevUserIdRef.current !== currentUserId) {
      // User changed (logout → login, or different user)
      setMessages([]);
      setInput("");
      setHistoryLoaded(false);
      setIsOpen(false);
      stopSpeaking();
    }
    prevUserIdRef.current = currentUserId;
  }, [user?.id, stopSpeaking]);

  // Also reset on logout (isAuthenticated goes false)
  useEffect(() => {
    if (!isAuthenticated) {
      setMessages([]);
      setInput("");
      setHistoryLoaded(false);
      setIsOpen(false);
      stopSpeaking();
    }
  }, [isAuthenticated, stopSpeaking]);

  // ── Auto-scroll on new messages ──
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Load history when widget opens ──
  useEffect(() => {
    if (isOpen && isAuthenticated && !historyLoaded) {
      loadHistory();
    }
  }, [isOpen, isAuthenticated]);

  // ── Focus input when opened ──
  useEffect(() => {
    if (isOpen && !loading) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, loading]);

  // ── Cleanup recording on unmount ──
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      stopSpeaking();
    };
  }, [stopSpeaking]);

  /* ── Load conversation history ── */
  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/history`, authHeaders);
      const history = res.data.history || [];
      if (history.length > 0) {
        setMessages(
          history.map((h) => ({
            id: h.ts + h.role,
            role: h.role,
            content: h.content,
          }))
        );
      }
      setHistoryLoaded(true);
    } catch (err) {
      console.error("Failed to load bot history:", err);
      setHistoryLoaded(true);
    }
  };

  /* ── TTS via OpenAI streaming backend ── */
  const speakText = useCallback(async (text) => {
    if (!ttsEnabled || !text) return;
    stopSpeaking();

    try {
      setIsSpeaking(true);

      const response = await fetch(`${API}/tts`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ message: text }),
      });

      if (!response.ok) throw new Error(`TTS failed: ${response.status}`);

      // ── Try real-time streaming via MediaSource (Chrome/Edge) ──
      const canStream =
        typeof MediaSource !== "undefined" &&
        MediaSource.isTypeSupported("audio/mpeg");

      if (canStream && response.body) {
        const mediaSource = new MediaSource();
        const audio = new Audio();
        audio.src = URL.createObjectURL(mediaSource);
        ttsAudioRef.current = audio;

        mediaSource.addEventListener("sourceopen", async () => {
          const sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
          const reader = response.body.getReader();
          let done = false;
          const pendingChunks = [];
          let appending = false;

          const appendNext = () => {
            if (appending || pendingChunks.length === 0) return;
            if (mediaSource.readyState !== "open") return;
            appending = true;
            sourceBuffer.appendBuffer(pendingChunks.shift());
          };

          sourceBuffer.addEventListener("updateend", () => {
            appending = false;
            if (pendingChunks.length > 0) {
              appendNext();
            } else if (done && mediaSource.readyState === "open") {
              mediaSource.endOfStream();
            }
          });

          // Start playback as soon as we have some data buffered
          let playStarted = false;

          while (true) {
            const { value, done: readerDone } = await reader.read();
            if (readerDone) {
              done = true;
              // If no more pending chunks and not appending, end now
              if (!appending && pendingChunks.length === 0 && mediaSource.readyState === "open") {
                mediaSource.endOfStream();
              }
              break;
            }
            pendingChunks.push(value.buffer);
            appendNext();

            // Start playing after first chunk arrives
            if (!playStarted) {
              playStarted = true;
              audio.play().catch(() => { });
            }
          }
        });

        audio.onended = () => {
          setIsSpeaking(false);
          URL.revokeObjectURL(audio.src);
          ttsAudioRef.current = null;
        };
        audio.onerror = () => {
          setIsSpeaking(false);
          URL.revokeObjectURL(audio.src);
          ttsAudioRef.current = null;
        };
      } else {
        // ── Fallback: download full blob then play (Safari / Firefox) ──
        const audioBlob = await response.blob();
        if (!audioBlob || audioBlob.size === 0) {
          setIsSpeaking(false);
          return;
        }
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        ttsAudioRef.current = audio;

        audio.onended = () => {
          setIsSpeaking(false);
          URL.revokeObjectURL(audioUrl);
          ttsAudioRef.current = null;
        };
        audio.onerror = () => {
          setIsSpeaking(false);
          URL.revokeObjectURL(audioUrl);
          ttsAudioRef.current = null;
        };
        await audio.play();
      }
    } catch (err) {
      console.error("TTS error:", err?.message);
      setIsSpeaking(false);
    }
  }, [ttsEnabled, token, stopSpeaking]);

  /* ── Send text message ── */
  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    // ── "stop" command: stop speech and reply with a kind message ──
    const stopWords = ["stop", "stop speaking", "stop talking", "be quiet", "shut up", "silence", "enough"];
    if (stopWords.includes(text.toLowerCase())) {
      stopSpeaking();
      const kindMsg = {
        id: Date.now().toString() + "_bot",
        role: "assistant",
        content: "Sure! I've stopped speaking. I hope that was helpful — feel free to ask anything else whenever you're ready! 😊",
      };
      setMessages((prev) => [...prev, { id: Date.now().toString(), role: "user", content: text }, kindMsg]);
      setInput("");
      return;
    }

    const userMsg = { id: Date.now().toString(), role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, { message: text }, authHeaders);
      const botMsg = {
        id: Date.now().toString() + "_bot",
        role: "assistant",
        content: res.data.response,
      };
      setMessages((prev) => [...prev, botMsg]);

      // TTS
      if (ttsEnabled) speakText(res.data.response);
    } catch (err) {
      const errMsg = err.response?.data?.detail || "Something went wrong. Please try again.";
      setMessages((prev) => [
        ...prev,
        { id: Date.now().toString() + "_err", role: "assistant", content: `⚠️ ${errMsg}` },
      ]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, authHeaders, ttsEnabled, speakText, stopSpeaking]);

  /* ── Handle keyboard input ── */
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  /* ── Voice recording (MediaRecorder → backend STT) ── */
  const [micError, setMicError] = useState("");

  const startRecording = async () => {
    setMicError("");
    try {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setMicError("Your browser does not support microphone access. Please use Chrome or Edge.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await sendVoiceMessage(audioBlob);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch (err) {
      console.error("Mic access error:", err);
      if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        setMicError("No microphone found. Please connect a microphone and try again.");
      } else if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setMicError("Microphone access denied. Please allow microphone permission in your browser settings.");
      } else if (err.name === "NotReadableError") {
        setMicError("Microphone is in use by another application. Please close it and try again.");
      } else {
        setMicError(`Microphone error: ${err.message || "Unknown error"}`);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const sendVoiceMessage = async (audioBlob) => {
    setLoading(true);
    // Optimistic user message
    const placeholderMsg = {
      id: Date.now().toString(),
      role: "user",
      content: "🎤 Voice message...",
      isPlaceholder: true,
    };
    setMessages((prev) => [...prev, placeholderMsg]);

    try {
      const formData = new FormData();
      formData.append("audio_file", audioBlob, "recording.webm");

      const res = await axios.post(`${API}/voice`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });

      // Replace placeholder with actual transcribed text
      setMessages((prev) =>
        prev.map((m) =>
          m.id === placeholderMsg.id
            ? { ...m, content: res.data.transcribed_text || "🎤 Voice message", isPlaceholder: false, transcribed: null }
            : m
        )
      );

      const botMsg = {
        id: Date.now().toString() + "_bot",
        role: "assistant",
        content: res.data.response,
        transcribed: res.data.transcribed_text,
      };
      setMessages((prev) => [...prev, botMsg]);

      if (ttsEnabled) speakText(res.data.response);
    } catch (err) {
      // Remove placeholder on error
      setMessages((prev) => prev.filter((m) => m.id !== placeholderMsg.id));
      const errMsg = err.response?.data?.detail || "Voice processing failed.";
      setMessages((prev) => [
        ...prev,
        { id: Date.now().toString() + "_err", role: "assistant", content: `⚠️ ${errMsg}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  /* ── Clear history ── */
  const clearHistory = async () => {
    try {
      stopSpeaking();                       // ← stop voice on history clear
      await axios.delete(`${API}/history`, authHeaders);
      setMessages([]);
    } catch (err) {
      console.error("Failed to clear history:", err);
    }
  };

  /* ── Format recording time ── */
  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  /* ── Don't render if not authenticated ── */
  if (!isAuthenticated) return null;

  /* ═══════════════════════════════════════════════════════════════════════ */
  return (
    <>
      {/* ── Floating Action Button ── */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-[#F87217] to-[#FF9A4E] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center group"
          title="Marketplace Assistant"
        >
          <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
          {/* Pulse ring */}
          <span className="absolute inset-0 rounded-full bg-[#F87217] opacity-30 animate-ping pointer-events-none" />
        </button>
      )}

      {/* ── Chat Window ── */}
      {isOpen && (
        <div className="fixed bottom-6 left-6 z-50 w-[380px] h-[540px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300">
          {/* ── Header ── */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#F87217] to-[#FF9A4E] text-white flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm leading-tight">Marketplace Assistant</h3>
                <p className="text-[10px] opacity-80">Ask me about agents, pricing & more</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Stop speaking button — visible while TTS is playing */}
              {isSpeaking && (
                <button
                  onClick={stopSpeaking}
                  className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors animate-pulse"
                  title="Stop speaking"
                >
                  <StopCircle className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setTtsEnabled(!ttsEnabled)}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                title={ttsEnabled ? "Mute voice responses" : "Enable voice responses"}
              >
                {ttsEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              {messages.length > 0 && (
                <button
                  onClick={clearHistory}
                  className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                  title="Clear conversation"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => {
                  setIsOpen(false);
                  stopSpeaking();
                }}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                title="Minimize"
              >
                <Minus className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  stopSpeaking();
                }}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* ── Messages area ── */}
          <ScrollArea className="flex-1 px-4 py-3">
            {messages.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center h-full text-center pt-12">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F87217]/10 to-[#FF9A4E]/10 flex items-center justify-center mb-4">
                  <Bot className="w-7 h-7 text-[#F87217]" />
                </div>
                <h4 className="font-semibold text-slate-800 text-sm mb-1">
                  Hi{user?.name ? `, ${user.name.split(" ")[0]}` : ""}! 👋
                </h4>
                <p className="text-xs text-slate-500 max-w-[240px] mb-4">
                  I can help you find the right AI agent, explain pricing plans, or guide you around the marketplace.
                </p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {["What agents do you have?", "Explain pricing plans", "Help me find an agent"].map((q) => (
                    <button
                      key={q}
                      onClick={() => {
                        setInput(q);
                        setTimeout(() => {
                          setInput(q);
                          sendMessageDirect(q);
                        }, 50);
                      }}
                      className="px-2.5 py-1 rounded-full bg-slate-100 hover:bg-[#F87217]/10 text-slate-600 hover:text-[#F87217] text-[11px] transition-colors border border-slate-200 hover:border-[#F87217]/30"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <MessageBubble key={msg.id} msg={msg} />
            ))}

            {loading && (
              <div className="flex gap-2 justify-start mb-3">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#F87217] to-[#FF9A4E] flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-slate-100 rounded-xl rounded-bl-sm px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:0ms]" />
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:150ms]" />
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:300ms]" />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </ScrollArea>

          {/* ── Input area ── */}
          <div className="flex-shrink-0 border-t border-slate-200 p-3 bg-white">
            {/* Mic error banner */}
            {micError && (
              <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                <MicOff className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="flex-1">{micError}</span>
                <button onClick={() => setMicError("")} className="text-red-400 hover:text-red-600">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {isRecording ? (
              /* Recording state */
              <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-red-50 rounded-xl border border-red-200">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-sm text-red-600 font-medium">Recording {formatTime(recordingTime)}</span>
                </div>
                <Button
                  size="sm"
                  onClick={stopRecording}
                  className="rounded-full w-10 h-10 p-0 bg-red-500 hover:bg-red-600 text-white"
                  title="Stop recording & send"
                >
                  <MicOff className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              /* Normal input state */
              <div className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about agents, pricing..."
                    disabled={loading}
                    rows={1}
                    className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#F87217]/40 focus:border-[#F87217] disabled:opacity-50 max-h-[80px] overflow-y-auto transition-colors"
                    style={{
                      height: "auto",
                      minHeight: "40px",
                    }}
                    onInput={(e) => {
                      e.target.style.height = "auto";
                      e.target.style.height = Math.min(e.target.scrollHeight, 80) + "px";
                    }}
                  />
                </div>
                <Button
                  size="sm"
                  onClick={startRecording}
                  disabled={loading}
                  className="rounded-full w-10 h-10 p-0 bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200"
                  title="Voice input"
                >
                  <Mic className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={sendMessage}
                  disabled={loading || !input.trim()}
                  className="rounded-full w-10 h-10 p-0 bg-[#F87217] hover:bg-[#C65B12] text-white disabled:opacity-50"
                  title="Send message"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

  /* ── Direct send helper (for quick-action chips) ── */
  function sendMessageDirect(text) {
    if (!text.trim() || loading) return;
    const userMsg = { id: Date.now().toString(), role: "user", content: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    axios
      .post(`${API}/chat`, { message: text.trim() }, authHeaders)
      .then((res) => {
        const botMsg = {
          id: Date.now().toString() + "_bot",
          role: "assistant",
          content: res.data.response,
        };
        setMessages((prev) => [...prev, botMsg]);
        if (ttsEnabled) speakText(res.data.response);
      })
      .catch((err) => {
        const errMsg = err.response?.data?.detail || "Something went wrong.";
        setMessages((prev) => [
          ...prev,
          { id: Date.now().toString() + "_err", role: "assistant", content: `⚠️ ${errMsg}` },
        ]);
      })
      .finally(() => setLoading(false));
  }
}
